import * as React from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown } from "lucide-react";
import { craneByWorkorderOptions } from "@/api/workorders/query-options";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface CranePickerProps {
  workorderId: string | null;
  value: { id: string; label: string } | null;
  onChange: (v: { id: string; label: string } | null) => void;
  className?: string;
}

export function CranePicker({ workorderId, value, onChange, className }: CranePickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const { data: cranes = [] } = useQuery({
    ...craneByWorkorderOptions(workorderId ?? ""),
    enabled: !!workorderId,
  });

  const isDisabled = !workorderId;

  const triggerLabel = isDisabled
    ? t("crane.picker.workorderRequired")
    : (value?.label ?? t("crane.picker.placeholder"));

  return (
    <Popover open={open && !isDisabled} onOpenChange={(o) => !isDisabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <Button
          variant="default"
          role="combobox"
          aria-expanded={open}
          disabled={isDisabled}
          className={cn("w-full justify-between font-normal", className)}
        >
          <span className={cn("truncate", isDisabled && "text-ink-400")}>{triggerLabel}</span>
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder={t("crane.picker.placeholder")} />
          <CommandList>
            <CommandEmpty>{t("crane.picker.empty")}</CommandEmpty>
            {cranes.length > 0 && (
              <CommandGroup>
                {cranes.map((crane) => {
                  const label = `${crane.erpCode} — ${crane.description}`;
                  const isSelected = value?.id === crane.id;
                  return (
                    <CommandItem
                      key={crane.id}
                      value={crane.erpCode}
                      onSelect={() => {
                        onChange({ id: crane.id, label });
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn("mr-2 size-4", isSelected ? "opacity-100" : "opacity-0")}
                      />
                      {label}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
